import { isResource } from '@medplum/core'
import type { Basic, Practitioner, Reference } from '@medplum/fhirtypes'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  BASIC_CODE_SYSTEM,
  PARTICIPANT_VIEW_DISPLAY_SYSTEM,
  PARTICIPANT_VIEW_FILTERS_SYSTEM,
  VIEW_ACCESS_TAG_SYSTEM,
  VIEW_STATUS_TAG_SYSTEM,
} from '#src/codeSystems.ts'
import { Extension } from '#src/decorators.ts'
import { View, type ViewAccess } from '#src/models/view.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class ParticipantView extends View {
  static isInstance(value: unknown): value is Basic {
    return (
      isResource(value) &&
      isOfType(value, 'Basic') &&
      hasCodeableConcept(value.code, codeableConcept(ParticipantView.code))
    )
  }

  constructor({
    name,
    filters,
    display,
    owner,
    access,
  }: {
    name: string
    filters: string
    owner: Reference<Practitioner>
    display?: string
    access?: ViewAccess
  }) {
    super({
      code: codeableConcept(ParticipantView.code),
      name,
      owner,
      access,
    })
    this.filters = filters
    this.display = display
  }

  @Extension({
    url: PARTICIPANT_VIEW_FILTERS_SYSTEM,
    kind: 'valueString',
  })
  filters: string

  @Extension({
    url: PARTICIPANT_VIEW_DISPLAY_SYSTEM,
    kind: 'valueString',
  })
  display?: string

  static queryForPractitioner(
    practitioner: Reference<Practitioner>
  ): QueryTypes {
    return {
      code: `${ParticipantView.code.system}|${ParticipantView.code.code}`,
      _tag: `${VIEW_STATUS_TAG_SYSTEM}|${View.status.active.code}`,
      _filter: `(_tag eq ${VIEW_ACCESS_TAG_SYSTEM}|${practitioner.reference} or _tag eq ${VIEW_ACCESS_TAG_SYSTEM}|shared)`,
      _sort: 'identifier',
      _count: '1000',
    }
  }

  static code = {
    system: BASIC_CODE_SYSTEM,
    code: 'participant-view',
    display: 'Participant View',
  }
}
